import React, { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { collection, doc, getDoc, getDocs, limit, orderBy, query, startAfter, where } from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { useHistory, useLocation, useParams } from "react-router-dom";
import Navbar from "../Components/Navbar/navbar";
import CabecalhoDetalhes from "../Components/CabecalhoDetalhes/CabecalhoDetalhes";
import { db, storage } from "../Config/firebase";
import { compressImage } from "../../utils/compressImage";
import RelatorioAdesividadePrint from "./RelatorioAdesividadePrint";
import { consultaTenantAtual } from "../Utils/tenant";
import { atualizarDocumentoComMesclagem } from "../Utils/concorrenciaFirestore";

function normalizarLigantes(ligantes) {
  const base = Array.isArray(ligantes) && ligantes.length ? ligantes : [
    { id: `ligante-${Date.now()}`, nome: "", resultado: "", amostras: [{}, {}] },
  ];
  return base.map((ligante, index) => ({
    ...ligante,
    id: ligante.id || `ligante-${index + 1}`,
    amostras: [0, 1].map((indice) => ({ ...(ligante.amostras?.[indice] || {}) })),
  }));
}

const tamanhoPaginaMateriais = 50;

function clonarEnsaio(valor) {
  return valor ? JSON.parse(JSON.stringify(valor)) : null;
}

const larguraRecorte = 1280;
const alturaRecorte = 720;

function carregarImagem(url) {
  return new Promise((resolve, reject) => {
    const imagem = new Image();
    imagem.onload = () => resolve(imagem);
    imagem.onerror = () => reject(new Error("Não foi possível carregar a imagem."));
    imagem.src = url;
  });
}

async function gerarFotoRecortada({ url, zoom, deslocamentoX, deslocamentoY }) {
  const imagem = await carregarImagem(url);
  const canvas = document.createElement("canvas");
  canvas.width = larguraRecorte;
  canvas.height = alturaRecorte;
  const contexto = canvas.getContext("2d");

  const escalaBase = Math.max(
    larguraRecorte / imagem.naturalWidth,
    alturaRecorte / imagem.naturalHeight
  );
  const escala = escalaBase * zoom;
  const larguraImagem = imagem.naturalWidth * escala;
  const alturaImagem = imagem.naturalHeight * escala;
  const limiteX = Math.max(0, (larguraImagem - larguraRecorte) / 2);
  const limiteY = Math.max(0, (alturaImagem - alturaRecorte) / 2);
  const x = (larguraRecorte - larguraImagem) / 2 + deslocamentoX * limiteX;
  const y = (alturaRecorte - alturaImagem) / 2 + deslocamentoY * limiteY;

  contexto.drawImage(imagem, x, y, larguraImagem, alturaImagem);

  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", 0.88)
  );
  if (!blob) throw new Error("Não foi possível recortar a imagem.");

  return new File([blob], "adesividade-recortada.jpg", { type: "image/jpeg" });
}

export default function DetalhesAdesividade() {
  const { adesividadeId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const areaRecorteRef = useRef(null);
  const arrasteRef = useRef(null);
  const ensaioOriginalConcorrenciaRef = useRef(null);
  const [ensaio, setEnsaio] = useState(null);
  const [ensaioSalvo, setEnsaioSalvo] = useState(null);
  const [materiais, setMateriais] = useState([]);
  const [materiaisTodos, setMateriaisTodos] = useState([]);
  const [ultimoMaterialTodos, setUltimoMaterialTodos] = useState(null);
  const [paginaMateriaisTodos, setPaginaMateriaisTodos] = useState(0);
  const [temMaisMateriaisTodos, setTemMaisMateriaisTodos] = useState(false);
  const [carregandoMateriaisTodos, setCarregandoMateriaisTodos] = useState(false);
  const [ligantesComTodos, setLigantesComTodos] = useState({});
  const [arquivos, setArquivos] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [mensagem, setMensagem] = useState("");
  const [modoPrint, setModoPrint] = useState(false);
  const [modoEdicao, setModoEdicao] = useState(Boolean(location.state?.modoEdicao));
  const [recorte, setRecorte] = useState({
    open: false,
    file: null,
    url: "",
    liganteIndex: null,
    amostraIndex: null,
    tipo: "",
    zoom: 1,
    deslocamentoX: 0,
    deslocamentoY: 0,
    larguraImagem: 0,
    alturaImagem: 0,
  });

  useEffect(() => {
    carregar();
  }, [adesividadeId]);

  async function carregarMateriaisDoEnsaio(dadosEnsaio) {
    const filtros = [];
    if (dadosEnsaio?.obraId) {
      filtros.push(where("ObraId", "==", dadosEnsaio.obraId));
    } else if (dadosEnsaio?.clienteId) {
      filtros.push(where("ClienteId", "==", dadosEnsaio.clienteId));
    }

    const snap = await getDocs(
      consultaTenantAtual(query(
        collection(db, "tblListaMateriaisCadastrados"),
        ...filtros,
        limit(tamanhoPaginaMateriais)
      ))
    );
    setMateriais(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
  }

  async function carregarMateriaisTodos({ reiniciar = false } = {}) {
    if (carregandoMateriaisTodos) return;

    setCarregandoMateriaisTodos(true);
    try {
      const restricoes = [
        orderBy("codigo", "desc"),
        ...(reiniciar || !ultimoMaterialTodos ? [] : [startAfter(ultimoMaterialTodos)]),
        limit(tamanhoPaginaMateriais + 1),
      ];
      const snap = await getDocs(consultaTenantAtual(query(collection(db, "tblListaMateriaisCadastrados"), ...restricoes)));
      const docsPagina = snap.docs.slice(0, tamanhoPaginaMateriais);
      const lista = docsPagina.map((item) => ({ id: item.id, ...item.data() }));

      setMateriaisTodos((anteriores) => (reiniciar ? lista : [...anteriores, ...lista]));
      setUltimoMaterialTodos(docsPagina[docsPagina.length - 1] || null);
      setPaginaMateriaisTodos((paginaAtual) => (reiniciar ? 1 : paginaAtual + 1));
      setTemMaisMateriaisTodos(snap.docs.length > tamanhoPaginaMateriais);
    } finally {
      setCarregandoMateriaisTodos(false);
    }
  }

  async function carregar() {
    const snap = await getDoc(doc(db, "tblAdesividadeLiganteBetuminoso", adesividadeId));
    if (!snap.exists()) return;
    const dados = { id: snap.id, ...snap.data(), ligantes: normalizarLigantes(snap.data().ligantes) };
    setEnsaio(dados);
    setEnsaioSalvo(clonarEnsaio(dados));
    ensaioOriginalConcorrenciaRef.current = dados;
    await carregarMateriaisDoEnsaio(dados);
  }

  function atualizarLigante(indice, campo, valor) {
    setEnsaio((anterior) => ({
      ...anterior,
      ligantes: anterior.ligantes.map((item, itemIndice) =>
        itemIndice === indice ? { ...item, [campo]: valor } : item
      ),
    }));
  }

  function selecionarLigante(indice, material) {
    setEnsaio((anterior) => ({
      ...anterior,
      ligantes: anterior.ligantes.map((ligante, itemIndice) =>
        itemIndice === indice
          ? {
              ...ligante,
              materialId: material?.id || "",
              codigoMaterial: material?.codigo || "",
              nome: material?.Descricao || "",
              localColeta: material?.LocalColeta || "",
              procedencia: material?.Fornecedor || "",
            }
          : ligante
      ),
    }));
  }

  function chaveLigante(ligante, indice) {
    return ligante.id || `ligante-${indice}`;
  }

  function alternarMateriaisTodos(ligante, indice, marcado) {
    const chave = chaveLigante(ligante, indice);
    setLigantesComTodos((anteriores) => ({ ...anteriores, [chave]: marcado }));
    if (marcado && materiaisTodos.length === 0) {
      carregarMateriaisTodos({ reiniciar: true });
    }
  }

  function materialSelecionado(ligante) {
    if (!ligante.materialId) return null;
    return (
      [...materiais, ...materiaisTodos].find((material) => material.id === ligante.materialId) || {
        id: ligante.materialId,
        codigo: ligante.codigoMaterial || "",
        Descricao: ligante.nome || "",
        LocalColeta: ligante.localColeta || "",
        Fornecedor: ligante.procedencia || "",
      }
    );
  }

  function opcoesLigante(ligante, indice) {
    const usarTodos = Boolean(ligantesComTodos[chaveLigante(ligante, indice)]);
    const base = usarTodos ? materiaisTodos : materiais;
    const opcoes = base.filter((material) => material.id !== ensaio?.materialId);
    const selecionado = materialSelecionado(ligante);

    if (selecionado && !opcoes.some((material) => material.id === selecionado.id)) {
      return [selecionado, ...opcoes];
    }
    return opcoes;
  }

  function selecionarFoto(liganteIndex, amostraIndex, tipo, file) {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setRecorte({
      open: true,
      file,
      url,
      liganteIndex,
      amostraIndex,
      tipo,
      zoom: 1,
      deslocamentoX: 0,
      deslocamentoY: 0,
      larguraImagem: 0,
      alturaImagem: 0,
    });
  }

  function fecharRecorte() {
    if (recorte.url) URL.revokeObjectURL(recorte.url);
    setRecorte({
      open: false,
      file: null,
      url: "",
      liganteIndex: null,
      amostraIndex: null,
      tipo: "",
      zoom: 1,
      deslocamentoX: 0,
      deslocamentoY: 0,
      larguraImagem: 0,
      alturaImagem: 0,
    });
  }

  async function confirmarRecorte() {
    try {
      const file = await gerarFotoRecortada(recorte);
      const chave = `${recorte.liganteIndex}-${recorte.amostraIndex}-${recorte.tipo}`;
      const preview = URL.createObjectURL(file);

      setArquivos((anterior) => {
        if (anterior[chave]?.preview) {
          URL.revokeObjectURL(anterior[chave].preview);
        }
        return {
          ...anterior,
          [chave]: { file, preview },
        };
      });
      fecharRecorte();
    } catch (error) {
      setMensagem(error.message);
    }
  }

  function calcularLayoutRecorte() {
    const area = areaRecorteRef.current;
    if (!area || !recorte.larguraImagem || !recorte.alturaImagem) return null;

    const larguraArea = area.clientWidth;
    const alturaArea = area.clientHeight;
    const escalaBase = Math.max(
      larguraArea / recorte.larguraImagem,
      alturaArea / recorte.alturaImagem
    );
    const escala = escalaBase * recorte.zoom;
    const larguraImagem = recorte.larguraImagem * escala;
    const alturaImagem = recorte.alturaImagem * escala;

    return {
      larguraImagem,
      alturaImagem,
      limiteX: Math.max(0, (larguraImagem - larguraArea) / 2),
      limiteY: Math.max(0, (alturaImagem - alturaArea) / 2),
    };
  }

  function estiloImagemRecorte() {
    const layout = calcularLayoutRecorte();
    if (!layout) return { visibility: "hidden" };

    return {
      position: "absolute",
      top: "50%",
      left: "50%",
      width: layout.larguraImagem,
      height: layout.alturaImagem,
      maxWidth: "none",
      transform: `translate(-50%, -50%) translate(${recorte.deslocamentoX * layout.limiteX}px, ${recorte.deslocamentoY * layout.limiteY}px)`,
      userSelect: "none",
      pointerEvents: "none",
    };
  }

  function iniciarArraste(event) {
    const layout = calcularLayoutRecorte();
    if (!layout) return;

    event.currentTarget.setPointerCapture(event.pointerId);
    arrasteRef.current = {
      pointerId: event.pointerId,
      inicioX: event.clientX,
      inicioY: event.clientY,
      deslocamentoX: recorte.deslocamentoX,
      deslocamentoY: recorte.deslocamentoY,
      limiteX: layout.limiteX,
      limiteY: layout.limiteY,
    };
  }

  function moverImagem(event) {
    const arraste = arrasteRef.current;
    if (!arraste || arraste.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - arraste.inicioX;
    const deltaY = event.clientY - arraste.inicioY;
    const proximoX = arraste.limiteX
      ? arraste.deslocamentoX + deltaX / arraste.limiteX
      : 0;
    const proximoY = arraste.limiteY
      ? arraste.deslocamentoY + deltaY / arraste.limiteY
      : 0;

    setRecorte((anterior) => ({
      ...anterior,
      deslocamentoX: Math.max(-1, Math.min(1, proximoX)),
      deslocamentoY: Math.max(-1, Math.min(1, proximoY)),
    }));
  }

  function finalizarArraste(event) {
    if (arrasteRef.current?.pointerId === event.pointerId) {
      arrasteRef.current = null;
    }
  }

  function limparArquivosTemporarios() {
    Object.values(arquivos).forEach((arquivo) => {
      if (arquivo?.preview) URL.revokeObjectURL(arquivo.preview);
    });
    setArquivos({});
  }

  function acrescentarEnsaio() {
    setEnsaio((anterior) => ({
      ...anterior,
      ligantes: [
        ...anterior.ligantes,
        {
          id: `ligante-${Date.now()}`,
          nome: "",
          resultado: "",
          amostras: [{}, {}],
        },
      ],
    }));
  }

  function removerEnsaio(indice) {
    if (ensaio.ligantes.length === 1) return;
    setEnsaio((anterior) => ({
      ...anterior,
      ligantes: anterior.ligantes.filter((item, itemIndice) => itemIndice !== indice),
    }));
    setArquivos((anterior) => {
      const proximos = {};
      Object.entries(anterior).forEach(([chave, arquivo]) => {
        const [liganteIndex] = chave.split("-").map(Number);
        if (liganteIndex === indice) {
          if (arquivo?.preview) URL.revokeObjectURL(arquivo.preview);
          return;
        }
        const novaChave = liganteIndex > indice
          ? chave.replace(/^\d+/, String(liganteIndex - 1))
          : chave;
        proximos[novaChave] = arquivo;
      });
      return proximos;
    });
  }

  function iniciarEdicao() {
    setMensagem("");
    setEnsaioSalvo(clonarEnsaio(ensaio));
    ensaioOriginalConcorrenciaRef.current = ensaio;
    setModoEdicao(true);
  }

  function cancelarEdicao() {
    limparArquivosTemporarios();
    setEnsaio(clonarEnsaio(ensaioSalvo));
    setMensagem("");
    setModoEdicao(false);
  }

  function fotoAtual(liganteIndex, amostraIndex, tipo) {
    const chave = `${liganteIndex}-${amostraIndex}-${tipo}`;
    const arquivo = arquivos[chave];
    if (arquivo?.preview) return arquivo.preview;
    return ensaio.ligantes[liganteIndex]?.amostras?.[amostraIndex]?.[tipo]?.url || "";
  }

  async function salvar() {
    if (ensaio.ligantes.some((ligante) => !ligante.materialId)) {
      setMensagem("Selecione todos os ligantes na Entrada de Materiais.");
      return;
    }

    setSalvando(true);
    setMensagem("");
    try {
      const ligantes = await Promise.all(ensaio.ligantes.map(async (ligante, liganteIndex) => ({
        ...ligante,
        amostras: await Promise.all(ligante.amostras.map(async (amostra, amostraIndex) => {
          const proxima = { ...amostra };
          for (const tipo of ["antes", "depois"]) {
            const chave = `${liganteIndex}-${amostraIndex}-${tipo}`;
            const arquivo = arquivos[chave]?.file;
            if (!arquivo) continue;
            const comprimido = await compressImage(arquivo, { maxWidth: 1400, maxHeight: 1000, quality: 0.78 });
            const liganteId = String(ligante.id || `ligante-${liganteIndex + 1}`).replace(/[^a-zA-Z0-9_-]/g, "");
            const caminho = `adesividade/${ensaio.id}/${liganteId}-${amostraIndex}-${tipo}.jpg`;
            const storageRef = ref(storage, caminho);
            await uploadBytes(storageRef, comprimido, { contentType: "image/jpeg" });
            const url = await getDownloadURL(storageRef);
            if (proxima[tipo]?.path && proxima[tipo].path !== caminho) {
              deleteObject(ref(storage, proxima[tipo].path)).catch(() => {});
            }
            proxima[tipo] = { url, path: caminho };
          }
          return proxima;
        })),
      })));

      const dados = await atualizarDocumentoComMesclagem({
        db,
        referencia: doc(db, "tblAdesividadeLiganteBetuminoso", ensaio.id),
        original: ensaioOriginalConcorrenciaRef.current || ensaioSalvo,
        atual: { ...ensaio, ligantes },
        preparar: (estadoMesclado) => {
          const preparado = { ...estadoMesclado };
          delete preparado.id;
          return preparado;
        },
        metadados: { atualizadoEm: new Date() },
      });
      const ensaioAtualizado = { ...dados, id: ensaio.id };
      setEnsaio(ensaioAtualizado);
      setEnsaioSalvo(clonarEnsaio(ensaioAtualizado));
      ensaioOriginalConcorrenciaRef.current = ensaioAtualizado;
      limparArquivosTemporarios();
      setMensagem("Ensaio salvo com sucesso.");
      setModoEdicao(false);
    } catch (error) {
      console.error(error);
      setMensagem(`Erro ao salvar: ${error.message}`);
    } finally {
      setSalvando(false);
    }
  }

  if (!ensaio) return null;
  if (modoPrint) {
    return <RelatorioAdesividadePrint ensaio={ensaio} onVoltar={() => setModoPrint(false)} />;
  }

  return (
    <>
      <Navbar />
      <CabecalhoDetalhes
        titulo="Adesividade ao Ligante Betuminoso"
        subtitulo="DNIT 452/2024-ME   Rev.02 - 10/09/2024"
      >
          {modoEdicao ? (
            <>
              <Button variant="outlined" onClick={cancelarEdicao} disabled={salvando}>
                Cancelar
              </Button>
              <Button variant="contained" onClick={salvar} disabled={salvando}>
                {salvando ? "Salvando..." : "Salvar"}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={() => setModoPrint(true)}
              >
                Visualizar relatório
              </Button>
              <Button variant="outlined" onClick={() => history.goBack()}>Voltar</Button>
              <Button variant="contained" startIcon={<EditIcon />} onClick={iniciarEdicao}>
                Editar
              </Button>
            </>
          )}
      </CabecalhoDetalhes>

      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: "auto" }}>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography fontWeight={700} mb={2}>DADOS DO CADASTRO</Typography>
          <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(3, 1fr)" }} gap={2}>
            <TextField label="Código" value={ensaio.codigo || ""} InputProps={{ readOnly: true }} />
            <TextField label="Cliente" value={ensaio.cliente || ""} InputProps={{ readOnly: true }} />
            <TextField label="Obra" value={ensaio.obra || ""} InputProps={{ readOnly: true }} />
            <TextField label="Material" value={ensaio.material || ""} InputProps={{ readOnly: true }} />
            <TextField label="Fornecedor / Procedência" value={ensaio.fornecedor || ""} InputProps={{ readOnly: true }} />
            <TextField label="Local da coleta" value={ensaio.localColeta || ""} InputProps={{ readOnly: true }} />
          </Box>
        </Paper>

        {ensaio.ligantes.map((ligante, liganteIndex) => (
          <Paper key={ligante.id || liganteIndex} sx={{ p: 2, mb: 3 }}>
            <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "2fr 1fr auto" }} gap={2} mb={3}>
              <Box>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(ligantesComTodos[chaveLigante(ligante, liganteIndex)])}
                        onChange={(event) => alternarMateriaisTodos(ligante, liganteIndex, event.target.checked)}
                        disabled={!modoEdicao}
                      />
                    }
                    label="Todos"
                    sx={{ m: 0, whiteSpace: "nowrap" }}
                  />
                  <Autocomplete
                    options={opcoesLigante(ligante, liganteIndex)}
                    value={materialSelecionado(ligante)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={(option) =>
                      `${option.codigo || ""} - ${option.Descricao || ""} - ${option.Fornecedor || ""}`
                    }
                    onChange={(event, value) => selecionarLigante(liganteIndex, value)}
                    disabled={!modoEdicao}
                    sx={{ flex: 1 }}
                    noOptionsText={
                      ligantesComTodos[chaveLigante(ligante, liganteIndex)]
                        ? "Nenhum material carregado neste lote"
                        : "Nenhum material encontrado para esta obra"
                    }
                    renderInput={(params) => (
                      <TextField {...params} label={`Ligante ${liganteIndex + 1}`} />
                    )}
                  />
                </Stack>
                {modoEdicao && ligantesComTodos[chaveLigante(ligante, liganteIndex)] && (
                  <Stack direction="row" spacing={1} alignItems="center" mt={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => carregarMateriaisTodos()}
                      disabled={carregandoMateriaisTodos || !temMaisMateriaisTodos}
                    >
                      {carregandoMateriaisTodos ? "Carregando..." : "Carregar mais"}
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      {paginaMateriaisTodos ? `${materiaisTodos.length} materiais carregados` : "Carregue a primeira página"}
                    </Typography>
                  </Stack>
                )}
              </Box>
              <TextField
                select
                label="Resultado"
                value={ligante.resultado || ""}
                onChange={(event) => atualizarLigante(liganteIndex, "resultado", event.target.value)}
                disabled={!modoEdicao}
              >
                <MenuItem value="">
                  <em>Selecione</em>
                </MenuItem>
                <MenuItem value="Satisfatória">Satisfatória</MenuItem>
                <MenuItem value="Não satisfatória">Não satisfatória</MenuItem>
              </TextField>
              {modoEdicao && ensaio.ligantes.length > 1 && (
                <Tooltip title="Remover este ensaio">
                  <IconButton
                    color="error"
                    onClick={() => removerEnsaio(liganteIndex)}
                    disabled={salvando}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {modoEdicao && (
              <Button
                size="small"
                startIcon={<OpenInNewIcon />}
                onClick={() => window.open("/app/cadastromateriais?novo=1", "_blank", "noopener,noreferrer")}
                sx={{ mb: 2 }}
              >
                Cadastrar ligante na Entrada de Materiais
              </Button>
            )}

            {ligante.amostras.map((amostra, amostraIndex) => (
              <Box key={amostraIndex} mb={3}>
                <Typography fontWeight={700} mb={1}>Amostra {amostraIndex + 1}</Typography>
                <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
                  {["antes", "depois"].map((tipo) => (
                    <Box key={tipo}>
                      <Typography variant="body2" fontWeight={700} mb={1}>
                        Aspecto visual {tipo}
                      </Typography>
                      <Button
                        variant="outlined"
                        component="label"
                        fullWidth
                        disabled={!modoEdicao || salvando}
                      >
                        Selecionar foto
                        <input hidden type="file" accept="image/*" onChange={(event) =>
                          selecionarFoto(liganteIndex, amostraIndex, tipo, event.target.files?.[0])
                        } />
                      </Button>
                      {fotoAtual(liganteIndex, amostraIndex, tipo) && (
                        <Box
                          component="img"
                          src={fotoAtual(liganteIndex, amostraIndex, tipo)}
                          alt={`Amostra ${amostraIndex + 1} ${tipo}`}
                          sx={{
                            display: "block",
                            mt: 1,
                            width: "100%",
                            aspectRatio: `${larguraRecorte} / ${alturaRecorte}`,
                            height: "auto",
                            objectFit: "contain",
                            backgroundColor: "#111",
                            borderRadius: 1,
                          }}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Paper>
        ))}

        {modoEdicao && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={acrescentarEnsaio}
            disabled={salvando}
            sx={{ mb: 3 }}
          >
            Acrescentar ensaio
          </Button>
        )}

        <TextField
          label="Observações"
          value={ensaio.observacoes || ""}
          onChange={(event) => setEnsaio({ ...ensaio, observacoes: event.target.value })}
          disabled={!modoEdicao}
          fullWidth
          multiline
          minRows={3}
        />
        {mensagem && <Typography sx={{ mt: 2 }} color={mensagem.includes("sucesso") ? "success.main" : "error"}>{mensagem}</Typography>}
      </Box>

      <Dialog open={recorte.open} onClose={fecharRecorte} fullWidth maxWidth="md">
        <DialogTitle>Recortar foto</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Arraste a foto para colocar a parte desejada dentro da área do relatório.
          </Typography>

          <Box
            ref={areaRecorteRef}
            onPointerDown={iniciarArraste}
            onPointerMove={moverImagem}
            onPointerUp={finalizarArraste}
            onPointerCancel={finalizarArraste}
            sx={{
              position: "relative",
              width: "100%",
              aspectRatio: `${larguraRecorte} / ${alturaRecorte}`,
              overflow: "hidden",
              backgroundColor: "#111",
              border: "2px solid",
              borderColor: "primary.main",
              borderRadius: 1,
              cursor: "grab",
              touchAction: "none",
              "&:active": { cursor: "grabbing" },
            }}
          >
            {recorte.url && (
              <Box
                component="img"
                src={recorte.url}
                alt="Pré-visualização do recorte"
                onLoad={(event) => {
                  const larguraImagem = event.currentTarget.naturalWidth;
                  const alturaImagem = event.currentTarget.naturalHeight;
                  setRecorte((anterior) => ({
                    ...anterior,
                    larguraImagem,
                    alturaImagem,
                  }));
                }}
                sx={estiloImagemRecorte()}
              />
            )}
          </Box>

          <Typography fontWeight={700} mt={3}>Zoom</Typography>
          <Slider
            value={recorte.zoom}
            min={1}
            max={3}
            step={0.05}
            onChange={(event, valor) => setRecorte((anterior) => ({ ...anterior, zoom: valor }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharRecorte}>Cancelar</Button>
          <Button variant="contained" onClick={confirmarRecorte}>Aplicar recorte</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
